import React, { useEffect, useState, useMemo } from "react";
import { useTable, usePagination, useGlobalFilter, useSortBy } from "react-table";
import { useNavigate } from "react-router-dom";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faEllipsisV, faPencil } from "@fortawesome/free-solid-svg-icons";
import "bootstrap/dist/css/bootstrap.min.css";
import Select from "react-select";

const PendingSIReport = ({ permissions }) => {
  const [securityIns, setSecurityIns] = useState([]);
  const code = localStorage.getItem("code");
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  const canAdd = useMemo(() => permissions?.write, [permissions]);
  const docFilterOptions = [
    { label: "All", value: "all" },
    { label: "Original / Copy", value: "original_copy" },
    { label: "Inward Memo", value: "inward_memo" },
    { label: "Paper / Kanta / Parchi", value: "paper_kanta" },
  ];

  const [docFilter, setDocFilter] = useState(docFilterOptions[0]); // default All

  /* ================= FETCH DATA ================= */
  useEffect(() => {
    const fetchSecurityIn = async () => {
      try {
        const res = await apiClient.get(`${SERVER_BASE}/getSecurityIn`, {
          params: { code },
          headers: { Authorization: `Bearer ${token}` },
        });
        setSecurityIns(res.data || []);
      } catch (err) {
        console.error("Failed to fetch Pending SI data", err);
      }
    };

    fetchSecurityIn();
  }, [code, token]);

  /* ================= ONLY PENDING ================= */
  const applyDocumentFilter = (data) => {
    if (!docFilter || docFilter.value === "all") return data;

    switch (docFilter.value) {
      case "original_copy":
        return data.filter(
          (i) =>
            i.documentStatus === "original" ||
            i.documentStatus === "copyDocs"
        );

      case "inward_memo":
        return data.filter(
          (i) => i.documentStatus === "Inward Entry Memo Issued"
        );

      case "paper_kanta":
        return data.filter(
          (i) => i.documentStatus === "Received on Paper / Kanta / Parchi"
        );

      default:
        return data;
    }
  };

  const pendingBills = useMemo(() => {
    const base = securityIns.filter((item) => !item.billsAdded?.trim());
    return applyDocumentFilter(base);
  }, [securityIns, docFilter]);

  /* ================= COLUMNS ================= */
  const columns = useMemo(() => {
    const cols = [
      {
        Header: "Date",
        accessor: "entryDate",
        Cell: ({ value }) =>
          value ? moment(value).format("DD-MMM-YY") : "-",
      },
      { Header: "SR #", accessor: "gsrs", Cell: ({ value }) => value || "-" },
      { Header: "Vehicle Entry", accessor: "vehicleNumber" },
      { Header: "Came From", accessor: "cameFrom" },
      {
        Header: "Document Type",
        accessor: "documentStatus",
        Cell: ({ value }) => {
          if (value === "original") return "Original Documents";
          if (value === "copyDocs") return "Copy of Documents";
          if (value === "Inward Entry Memo Issued") return "Inward Memo";
          if (value === "Received on Paper / Kanta / Parchi")
            return "Paper / Kanta / Parchi";
          return value || "-";
        },
      },
      { Header: "Name", accessor: "driverName" },
      { Header: "Mobile", accessor: "mobileNo" },
      { Header: "SI No", accessor: "siCode", Cell: ({ value }) => value || "-" },
      {
        Header: "Info",
        Cell: ({ row }) => (
          <div className="dropdown">
            <button
              className="btn btn-light btn-sm"
              data-bs-toggle="dropdown"
            >
              <FontAwesomeIcon icon={faEllipsisV} />
            </button>

            <ul className="dropdown-menu">
              <li className="dropdown-item text-muted">
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                {row.original.createdByName
                  ? `${row.original.createdByName} (${moment(
                    row.original.createdAt
                  ).format("DD-MMM-YY hh:mm A")})`
                  : "-"}
              </li>

              {row.original.updatedBy && (
                <li className="dropdown-item text-muted">
                  <FontAwesomeIcon icon={faPencil} className="me-2" />
                  {row.original.updatedByName
                    ? `${row.original.updatedByName} (${moment(
                      row.original.updatedAt
                    ).format("DD-MMM-YY hh:mm A")})`
                    : "-"}
                </li>
              )}
            </ul>
          </div>
        ),
      },
    ];

    if (canAdd) {
      cols.unshift({
        Header: "Action",
        Cell: ({ row }) => (
          <button
            className="btn btn-outline-success btn-sm"
            onClick={() =>
              navigate("../AddSiBill", { state: { rowData: row.original } })
            }
          >
            <FontAwesomeIcon icon={faPlus} /> Bills
          </button>
        ),
      });
    }

    return cols;
  }, [canAdd, navigate]);

  /* ================= TABLE ================= */
  const tableInstance = useTable(
    {
      columns,
      data: pendingBills,
      initialState: { pageIndex: 0, pageSize: 10 },
    },
    useGlobalFilter,
    useSortBy,       // 👈 ADD HERE
    usePagination
  );


  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state,
    setGlobalFilter,
    setPageSize,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    pageOptions,
  } = tableInstance;

  const { globalFilter, pageIndex, pageSize } = state;

  /* ================= UI ================= */
  return (
    <div className="formcontainer mt-5">
      <h4 className="mb-3">Pending SI Report</h4>

      <div className="d-flex gap-2 mb-3 align-items-center">
        <input
          value={globalFilter || ""}
          onChange={(e) => setGlobalFilter(e.target.value)}
          className="form-control"
          placeholder="Search..."
        />

        <Select
          options={docFilterOptions}
          value={docFilter}
          onChange={setDocFilter}
          placeholder="Filter Document Type"
          styles={{
            container: (base) => ({ ...base, width: 260 }),
            menu: (base) => ({ ...base, zIndex: 9999 }),
          }}
        />

        <select
          className="form-select"
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          style={{ width: "auto" }}
        >
          {[5, 10, 20, 50, 100].map((size) => (
            <option key={size} value={size}>
              Show {size}
            </option>
          ))}
        </select>
      </div>

      <div className="table-responsive">
        <table className="table table-striped" {...getTableProps()}>
          <thead className="table-light">
            {headerGroups.map((hg) => (
              <tr {...hg.getHeaderGroupProps()}>
                {hg.headers.map((col) => (
                  <th
                    {...col.getHeaderProps(col.getSortByToggleProps())}
                    style={{ cursor: col.canSort ? "pointer" : "default" }}
                  >
                    {col.render("Header")}
                    {col.canSort && (
                      <span className="ms-1">
                        {col.isSorted ? (col.isSortedDesc ? " 🔽" : " 🔼") : ""}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>


          <tbody {...getTableBodyProps()}>
            {page.length ? (
              page.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()}>
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={columns.length} className="text-center py-3">
                  No pending records
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-2">
        <button
          className="btn btn-secondary"
          onClick={previousPage}
          disabled={!canPreviousPage}
        >
          Previous
        </button>

        Page {pageIndex + 1} of {pageOptions.length}

        <button
          className="btn btn-secondary"
          onClick={nextPage}
          disabled={!canNextPage}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default PendingSIReport;
